import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { Button } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import * as templateBuilderActions from '../../modules/template-builder-ex';
import TemplateBuilderContainerEx from '../../components/template/builder/indexEx';
import { TemplatePage } from '../../components/common/pager';
import { TemplateHeader } from '../../components/template/templateHeader';
import { TemplateInput } from '../../components/template/templateInput';
import { TemplateCheckbox } from '../../components/template/templateCheckbox';
import { TemplateDropdown } from '../../components/template/templateDropdown';
import {
  countries,
  getOrganisationCountryOptions,
  getOrganizationPostcodeValidationModel,
  getOrganizationTaxIdValidationModel,
  normalizeOrganizationTaxId
} from '../../constants/countries';
import { selectIsFeatureFlagExplicitlyEnabled } from '../../modules/featureFlags/featureFlagSelectors';
import { FeatureFlagKeys } from '../../modules/featureFlags/featureFlagTypes';
import {
  compliancePagePath,
  hmrcCharityReferencePath,
  isCompliancePageVisible,
  orgDetailComponents,
  organizationAboutValidationModels,
  organizationComplianceValidationModels,
  complianceComponents
} from './constants';
import {
  getCharityRegulator,
  hmrcCharityReferenceValidationModel
} from '../../helpers/charityRegulatorHelper';
import { TemplateImageUpload } from '../../components/template/templateImageUpload';
import { TemplateColorPicker } from '../../components/template/templateColorPicker';
import { TemplateText } from '../../components/template/templateText';
import { uploadTypes, entityTypes } from '../../constants/imageUpload';
import { Form } from 'semantic-ui-react';
import TemplateStripeConnection from './template-stripe-connection';
import TemplateSalesforceConnection from './template-salesforce-connection';
import SwiftaidConnection from './swiftaidConnection';
import { selectIsSwiftaidConnectEnabled } from './swiftaidConnection/visibility';
import { withRouterV6 as withRouter } from '../../helpers/withRouterV6.jsx';
import { imageFormats } from '../../constants/imageUpload';
import config from '../../constants';
import { TemplateCustomVideoSelector } from '../../components/template/custom/templateCustomVideoSelector';
import { TemplateCustomMediaSelector } from '../../components/template/custom/templateCustomMediaSelector';
import { TemplateDefaultAmounts } from '../../components/template/custom/templateDefaultAmounts';
import { invertionTextColourOptions } from '../campaign/constants';
import { TemplateHorizontalRule } from '../../components/template/templateHorizontalRule';
import { getTimezoneOptions } from '../../helpers/timezoneOptionsHelper';
import * as templateHelper from '../../components/template/templateHelper';

import { useDonationSubscriptionSettings } from '../../components/template/useDonationSubscriptionSettings';
import { WebTrackingTemplate } from "../../components/webTracking/web-tracking";
import { InfoBox } from '../../components/common/infoBox';
import { Tabs } from '../../components/common/tabs';
import { Multicurrency } from '../../components/multicurrency';
import { TemplateToggle } from '../../components/template/templateToggle';

class OrganizationTemplateView extends Component {
  constructor(props) {
    super(props);

    this.required = {
      required: true
    };

    this.optional = {
      required: false
    };

    this.url = {
      required: true,
      url: true
    };

    this.optionalUrl = {
      url: true
    };

    this.defaultAmountsValidation = {
      defaultAmounts: true
    };

    this.photoPhormats = imageFormats;

    this.domainValidation = {
      required: true,
      domainUrl: true
    };

    this.state = {
      selectedDollarHandlesTab: "once-off",
    };

    if (!this.hasCountryValue(this.props)) {
      this.props.hideControls(orgDetailComponents);
    }
  }

  onChange = (path, value, item) => {
    this.props.setResultValue(path, value);
  };

  onCountryChange = (path, value, item) => {
    if (value && !this.hasCountryValue(this.props)) {
      this.props.showControls(orgDetailComponents);
    }

    if (countries[value] === countries.NZ) {
      this.props.showControls(complianceComponents);
    } else {
      this.props.hideControls(complianceComponents);
    }

    // Never clear on hide here: the page's path is the compliance object itself, so a
    // clear would wipe the HMRC charity reference (and with it any Swiftaid link).
    this.props.changeItemVisibility(
      compliancePagePath,
      isCompliancePageVisible(value, this.props.isUkCharityEnabled),
      false
    );

    // The API rejects an HMRC charity reference for every country other than the United
    // Kingdom.
    if (countries[value] !== countries.GB) {
      this.props.setResultValue(hmrcCharityReferencePath, undefined);
    }

    this.props.setResultValue(path, value);
  };

  // The tax ID is stored stripped of anything the selected country's format
  // cannot contain, so a labelled paste such as "EIN: 12-3456789" is kept as
  // the identifier itself rather than being saved as-is and refused.
  onTaxIdChange = (path, value) => {
    this.props.setResultValue(
      path,
      normalizeOrganizationTaxId(this.props.address?.country, value)
    );
  };

  // For the United Kingdom the tax ID is the charity registration number issued by the
  // charity regulator. It is stored
  // upper-cased so it matches what the API persists, and so the shared pattern and the
  // live regulator inference both see the same normalised value.
  onCharityRegulatorNumberChange = (path, value) => {
    const normalized = normalizeOrganizationTaxId(
      this.props.address?.country,
      value
    );
    this.props.setResultValue(
      path,
      normalized ? normalized.toUpperCase() : normalized
    );
  };

  getCountryOptions() {
    return getOrganisationCountryOptions(this.props.isUkCharityEnabled);
  }

  getDashboardOptions() {
    const titlePath = 'template.organisation.content.about.dashboard';
    return [
      { text: I18n.t(`${titlePath}.default`), value: null },
      { text: I18n.t(`${titlePath}.amountRaised`), value: 'amountRaised' },
      { text: I18n.t(`${titlePath}.alphabetical`), value: 'alphabetical' }
    ];
  }

  hasCountryValue = (props) => {
    return props.address && props.address.country;
  };

  // `countries` is keyed by country code, so an unrecognised code has to fall
  // back rather than be indexed into - the lookup returns undefined and reading
  // a descriptor off it would take the whole about page down.
  getTaxFieldTextDescriptor = () => {
    return (
      countries[this.props.address?.country]?.taxDescriptor ||
      countries.AU.taxDescriptor
    );
  };

  getTaxDeductibleFieldTextDescriptor = () => {
    if (this.props.address && this.props.address.country) {
      return `${this.props.address.country.toLowerCase()}-tax-deductible`;
    }
    return 'dgr';
  };

  handleCountryFocus = (evt) => {
    if (evt.target.getAttribute('autocomplete') === 'off') {
      evt.target.setAttribute('autocomplete', 'no');
    }
  };

  getColoursValidation = () => {
    const validationModel = [
      this.getChildValidation('template.value.primaryColor', this.required),
      this.getChildValidation('template.value.secondaryColor', this.required)
    ];
    return validationModel;
  };

  getChildValidation = (path, validation) => {
    return {
      path,
      validation
    };
  };

  onUseAsCustomOrganizationChange = (path, value) => {
    this.props.changeItemVisibility(
      'template.value.customDomain.domain',
      value,
      true
    );
    this.props.setResultValue(path, value);
  };

  onAllowCRMIntegrationChange = (path, value) => {
    this.props.changeItemVisibility('crmPlatforms', value, false);
    this.props.setResultValue(path, value);
  };

  onPreviewDashboardClick = () => {
    window.open(this.props.organization?.urlFull, '_blank');
  };

  render() {
    const country = this.props.address && this.props.address.country;
    // Drives the per-country street, city, state and postcode labels. `countries` is a map
    // keyed by country code, so fall back to Australia rather than an index, matching
    // getTaxFieldTextDescriptor.
    const countryKey = countries[country]?.key || countries.AU.key;

    // Swiftaid is a United Kingdom Gift Aid service, so the panel is offered to any GB
    // organisation. Unlike the UK charity fields below it is not gated on the UK
    // charity feature flag: an organisation already set to GB must still be able to
    // manage its link if that flag is later turned off.
    const isGbOrganization = countries[country] === countries.GB;

    // Hidden until Grassrootz's own Gift Aid agent registration with HMRC is approved,
    // apart from system admins testing it under their own flag. See
    // selectIsSwiftaidConnectEnabled.
    const isSwiftaidVisible =
      isGbOrganization && this.props.isSwiftaidConnectEnabled;

    // The United Kingdom is only selectable while the UK charity feature flag is on. For it
    // the about page's tax ID is the charity registration number (issued by the
    // charity regulator), and the compliance page carries the optional HMRC charity
    // reference. The same check hides the registered charity checkbox on the about page.
    const isUnitedKingdom = this.props.isUkCharityEnabled && isGbOrganization;
    const charityRegulator = isUnitedKingdom
      ? getCharityRegulator(this.props.taxId)
      : null;
    const taxIdTextPath = `template.organisation.content.about.${this.getTaxFieldTextDescriptor()}`;
    const charityRegulatorNamesPath =
      'template.organisation.content.compliance.charity-regulator-number.regulator';

    return (
      <TemplateBuilderContainerEx
        onSave={this.props.onSave}
        isLoading={this.props.isLoading}
        onNext={this.props.onNext}
        isNextLoading={this.props.isNextLoading}
        hidePublishButton={this.props.hidePublishButton}
        publishButtonLabel={this.props.publishButtonLabel}
      >
        <TemplatePage
          id="about"
          i18nKey="template.organisation.content.about.header"
        >
          <TemplateDropdown
            id="aboutCountry"
            path="address.country"
            search
            label="template.organisation.content.about.country.label"
            subtext="template.organisation.content.about.country.subtext"
            validation={organizationAboutValidationModels.country}
            onChange={this.onCountryChange}
            getOptions={() => this.getCountryOptions()}
            onFocus={this.handleCountryFocus}
          />

          <TemplateHeader
            as="h3"
            path="organizationDetilHeader"
            label="template.organisation.content.about.organisation-details-header"
          />

          <TemplateInput
            id="aboutOrganizationName"
            path="name"
            label="template.organisation.content.about.name.label"
            subtext="template.organisation.content.about.name.subtext"
            validation={organizationAboutValidationModels.name}
            onChange={this.onChange}
          />

          <TemplateInput
            id="aboutOrganisationLegalName"
            path="legalName"
            label="template.organisation.content.about.legal-name.label"
            subtext="template.organisation.content.about.legal-name.subtext"
            validation={organizationAboutValidationModels.legalName}
            onChange={this.onChange}
          />

          <TemplateInput
            id="aboutSubdomain"
            path="urlPath"
            label="template.organisation.content.about.subdomain.label"
            subtext="template.organisation.content.about.subdomain.subtext"
            validation={organizationAboutValidationModels.subdomain}
            onChange={this.onChange}
            subtextTranslationObject={{ domain: config.DOMAIN_URL }}
          />

          {/*
           * For a United Kingdom charity the subtext names the regulator once the number
           * matches one of the three formats.
           */}
          <TemplateInput
            id="aboutABN"
            path="taxId"
            label={`${taxIdTextPath}.label`} //'template.organisation.content.about.abn.label'
            subtext={
              charityRegulator
                ? `${charityRegulatorNamesPath}.detected`
                : `${taxIdTextPath}.subtext`
            }
            subtextTranslationObject={
              charityRegulator
                ? {
                    regulator: I18n.t(
                      `${charityRegulatorNamesPath}.${charityRegulator}`
                    )
                  }
                : undefined
            }
            validation={getOrganizationTaxIdValidationModel(
              this.props.address && this.props.address.country
            )}
            onChange={
              isUnitedKingdom
                ? this.onCharityRegulatorNumberChange
                : this.onTaxIdChange
            }
          />

          {/*
           * Hidden for United Kingdom charities, whose charitable status is captured by
           * the charity registration number above instead.
           */}
          {!isUnitedKingdom && (
            <TemplateCheckbox
              id="aboutDGR"
              path="template.value.hasDGR"
              inputLabel={`template.organisation.content.about.${this.getTaxDeductibleFieldTextDescriptor()}.label`}
              subtext={`template.organisation.content.about.${this.getTaxDeductibleFieldTextDescriptor()}.subtext`}
              onChange={this.onChange}
            />
          )}

          <TemplateInput
            id="aboutStreet"
            path="address.street"
            label={`template.organisation.content.about.street.${countryKey}.label`}
            validation={organizationAboutValidationModels.street}
            onChange={this.onChange}
          />

          <TemplateInput
            id="aboutCity"
            path="address.city"
            label={`template.organisation.content.about.city.${countryKey}.label`}
            validation={organizationAboutValidationModels.city}
            onChange={this.onChange}
          />

          <TemplateInput
            id="aboutState"
            path="address.state"
            label={`template.organisation.content.about.state.${countryKey}.label`}
            validation={organizationAboutValidationModels.state}
            onChange={this.onChange}
          />

          <TemplateInput
            id="aboutPostcode"
            path="address.postcode"
            label={`template.organisation.content.about.postcode.${countryKey}.label`}
            validation={getOrganizationPostcodeValidationModel(
              this.props.address && this.props.address.country
            )}
            onChange={this.onChange}
          />

          <TemplateDropdown
            id="aboutTimeZone"
            path="timezone"
            search
            label="template.organisation.content.about.timezone.label"
            subtext="template.organisation.content.about.timezone.subtext"
            validation={organizationAboutValidationModels.timeZone}
            onChange={this.onChange}
            getOptions={getTimezoneOptions}
          />

          {(this.props.isSystemAdmin || this.props.isOrgAdmin) && (
            <div path="publicDashboard">
              <TemplateHeader
                as="h3"
                path="organizationDetilHeader"
                label="template.organisation.content.about.dashboard-header"
              />

              <TemplateHeader
                as="h5"
                label="template.organisation.content.about.dashboard-subHeader"
              />

              <div className="field">
                <Button onClick={this.onPreviewDashboardClick}>
                  View my public dashboard
                </Button>
              </div>

              <TemplateDropdown
                id="aboutDashboardSort"
                defaultLabel="template.organisation.content.about.dashboard.default"
                path="template.value.dashboardSettings.sort"
                label="template.organisation.content.about.dashboard.label"
                onChange={this.onChange}
                getOptions={this.getDashboardOptions}
              />
            </div>
          )}
        </TemplatePage>

        <TemplatePage
          id="compliance"
          path="compliance"
          i18nKey="template.organisation.content.compliance.header"
        >
          <TemplateHeader
            as="h3"
            label="template.organisation.content.compliance.alternate-label"
          />

          <TemplateText
            size="small"
            label="template.organisation.content.compliance.alternate-description"
          />

          {isUnitedKingdom && (
            <TemplateInput
              id="complianceHmrcCharityReference"
              path={hmrcCharityReferencePath}
              label="template.organisation.content.compliance.hmrc-charity-reference.label"
              subtext="template.organisation.content.compliance.hmrc-charity-reference.subtext"
              validation={hmrcCharityReferenceValidationModel}
              onChange={this.onChange}
            />
          )}

          <TemplateInput
            path="compliance.alternateTaxId1"
            label={`template.organisation.content.compliance.alternateTaxId1.${country}`}
            onChange={this.onChange}
          />

          <TemplateInput
            path="compliance.alternateTaxId2"
            label={`template.organisation.content.compliance.alternateTaxId2.${country}`}
            onChange={this.onChange}
          />

          {/* Pathed so the New Zealand only signature section hides with its fields. */}
          <TemplateHorizontalRule path="compliance.authorizedPerson.name" />
          <TemplateHeader
            as="h3"
            path="compliance.authorizedPerson.name"
            label="template.organisation.content.compliance.signature-label"
          />

          <TemplateInput
            path="compliance.authorizedPerson.name"
            label={`template.organisation.content.compliance.authorizedPerson.name.${country}`}
            onChange={this.onChange}
            validation={
              organizationComplianceValidationModels.complianceAuthorizedPersonName
            }
          />

          <TemplateInput
            path="compliance.authorizedPerson.title"
            label={`template.organisation.content.compliance.authorizedPerson.title.${country}`}
            onChange={this.onChange}
            validation={
              organizationComplianceValidationModels.complianceAuthorizedPersonTitle
            }
          />

          <TemplateImageUpload
            path="compliance.authorizedPerson.signatureImagePath"
            entity={entityTypes.ORGANIZATION}
            uploadType={uploadTypes.ASSET}
            position="top"
            height={350}
            width={1500}
            label={`template.organisation.content.compliance.authorizedPerson.signature.${country}`}
            validation={
              organizationComplianceValidationModels.complianceAuthorizedPersonSignature
            }
            onChange={this.onChange}
          />
        </TemplatePage>

        <TemplatePage
          id="branding"
          i18nKey="template.organisation.content.branding.header"
        >
          <TemplateHeader
            as="h3"
            label="template.organisation.content.branding.header"
          />

          <InfoBox
            header="template.organisation.content.branding.info-box.header"
            content="template.organisation.content.branding.info-box.content"
          />

          <TemplateInput
            id="brandingOrganisationWebsite"
            path="template.value.organisationWebsite"
            label="template.organisation.content.branding.website.label"
            subtext="template.organisation.content.branding.website.subtext"
            validation={this.url}
            onChange={this.onChange}
          />

          <TemplateInput
            id="brandingMission"
            path="template.value.missionStatement"
            label="template.organisation.content.branding.mission.label"
            subtext="template.organisation.content.branding.mission.subtext"
            validation={this.required}
            onChange={this.onChange}
          />

          <TemplateImageUpload
            id="brandingLogo"
            path="mainImagePath"
            entity={entityTypes.ORGANIZATION}
            uploadType={uploadTypes.LOGO}
            position="left"
            height={350}
            width={350}
            label="template.organisation.content.branding.organisation-logo.label"
            subtext="template.organisation.content.branding.organisation-logo.subtext"
            validation={this.required}
            onChange={this.onChange}
          />

          <Form.Group validation={this.getColoursValidation()} widths="equal">
            <TemplateColorPicker
              id="brandingPrimaryColor"
              path="template.value.primaryColor"
              label="template.organisation.content.branding.color-primary.label"
              subtext="template.organisation.content.branding.color-primary.subtext"
              validation={this.required}
              onChange={this.onChange}
            />

            <TemplateColorPicker
              id="brandingSecondaryColor"
              path="template.value.secondaryColor"
              label="template.organisation.content.branding.color-secondary.label"
              subtext="template.organisation.content.branding.color-secondary.subtext"
              validation={this.required}
              onChange={this.onChange}
            />
          </Form.Group>
          <Form.Group widths="equal">
            <TemplateDropdown
              id="invertPrimaryTextColor"
              path="template.value.invertPrimaryTextColor"
              label="template.organisation.content.branding.invert-colors.invertPrimaryTextColor.label"
              onChange={this.onChange}
              options={invertionTextColourOptions}
            />

            <TemplateDropdown
              id="invertSecondaryTextColor"
              path="template.value.invertSecondaryTextColor"
              label="template.organisation.content.branding.invert-colors.invertSecondaryTextColor.label"
              onChange={this.onChange}
              options={invertionTextColourOptions}
            />
          </Form.Group>
        </TemplatePage>

        <TemplatePage
          id="advanced"
          i18nKey="template.organisation.content.advanced.page-title"
        >
          <WebTrackingTemplate
            type="organisation"
            path="template.value.webTracking"
            onChange={this.onChange}
          />

          <Multicurrency
            type="organization"
            onChange={this.onChange}
          />
        </TemplatePage>

        <TemplatePage
          id="donation-forms"
          path="donation-forms"
          i18nKey="template.organisation.content.donation-forms.header"
        >
          <TemplateHeader
            as="h3"
            label="template.organisation.content.donation-forms.header"
          />

          <TemplateHeader
            as="h5"
            label="template.organisation.content.donation-forms.amounts.header"
          />

          <InfoBox
            header="template.organisation.content.donation-forms.amounts.info-box.label"
            content="template.organisation.content.donation-forms.amounts.info-box.subtext"
          />

          <Tabs
            buttons={[
              { key: "once-off", label: "Once-off" },
              { key: "monthly", label: "Monthly" },
            ]}
            selectedTab={this.state.selectedDollarHandlesTab}
            onChangeSelectedTab={(newSelectedTab) => {
              this.setState({
                ...this.state,
                selectedDollarHandlesTab: newSelectedTab,
              });
            }}
          />

          <TemplateToggle
            customValue={this.state.selectedDollarHandlesTab === "once-off"}
          >
            <TemplateDefaultAmounts
              id="onceOffDonationDefaultAmounts"
              path="template.value.donationSetup.defaultAmounts"
              validation={this.defaultAmountsValidation}
              onChange={this.onChange}
            />
          </TemplateToggle>

          <TemplateToggle
            customValue={this.state.selectedDollarHandlesTab === "monthly"}
          >
            <TemplateDefaultAmounts
              id="recurringDonationDefaultAmounts"
              path="template.value.recurringDonationSetup.defaultAmounts"
              validation={this.defaultAmountsValidation}
              onChange={this.onChange}
            />

            <TemplateInput
              id="donationPageText"
              path="template.value.recurringDonationSetup.donationPageText"
              label="template.organisation.content.donation-forms.donation-page-text.label"
              subtext="template.organisation.content.donation-forms.donation-page-text.subtext"
              validation={this.optional}
              onChange={this.onChange}
            />

            <TemplateImageUpload
              id="donationHeroImage"
              path="template.value.recurringDonationSetup.donationHeroImage"
              entity={entityTypes.ORGANIZATION}
              uploadType={uploadTypes.HERO_IMAGE}
              position="top"
              height={350}
              width={1500}
              label="template.organisation.content.donation-forms.hero-image.label"
              subtext="template.organisation.content.donation-forms.hero-image.subtext"
              validation={this.optional}
              onChange={this.onChange}
            />

            <TemplateInput
              id="donationThankYouMessage"
              path="template.value.recurringDonationSetup.thankYouMessage"
              label="template.organisation.content.donation-forms.thank-you-message.label"
              subtext="template.organisation.content.donation-forms.thank-you-message.subtext"
              validation={this.optional}
              onChange={this.onChange}
            />

            <TemplateHeader
              as="h5"
              label="template.organisation.content.donation-forms.thank-you-media-heading.label"
            />

            <TemplateCustomMediaSelector
              onChange={this.onChange}
              path="template.value.recurringDonationSetup.selectedMediaType"
            >
              <TemplateCustomVideoSelector
                id="donationVideo"
                label="Video URL"
                radioLabel="template.organisation.content.donation-forms.donation-video.label"
                path="template.value.recurringDonationSetup.thankYouVideoUrl"
                subtext="template.organisation.content.donation-forms.donation-video.subtext"
                onChange={this.onChange}
              />

              <TemplateImageUpload
                id="donationImage"
                path="template.value.recurringDonationSetup.thankYouImageUrl"
                radioLabel="template.organisation.content.donation-forms.donation-image.label"
                entity={entityTypes.ORGANIZATION}
                uploadType={uploadTypes.THANK_YOU}
                position="top"
                height={350}
                width={1500}
                disableCropping
                onChange={this.onChange}
              />
            </TemplateCustomMediaSelector>

            <TemplateInput
              id="urlToReturn"
              path="template.value.recurringDonationSetup.urlToReturn"
              label="template.organisation.content.donation-forms.urlToReturn.label"
              subtext="template.organisation.content.donation-forms.urlToReturn.subtext"
              validation={this.optionalUrl}
              onChange={this.onChange}
            />

            <br />
            <TemplateHeader
              as="h3"
              label="template.organisation.content.donation-forms.behaviour.label"
            />
            <TemplateDropdown
              id="donationSubscriptionSettings"
              path="donationSubscriptionSettings"
              label="template.organisation.content.donation-forms.receipt.label"
              subtext="template.organisation.content.donation-forms.receipt.subtext"
              {...useDonationSubscriptionSettings({
                value: this.props.donationSubscriptionSettings,
                onChange: this.onChange
              })}
            />
          </TemplateToggle>
        </TemplatePage>

        <TemplatePage
          id="email-notification"
          path="email-notification"
          i18nKey="template.organisation.content.email-notifications.header"
        >
          <TemplateHeader
            as="h3"
            label="template.organisation.content.email-notifications.header"
          />

          <TemplateText
            size="small"
            label="template.organisation.content.email-notifications.subheader"
          />

          <TemplateHeader
            as="h4"
            label="template.organisation.content.email-notifications.accounting.title"
          />

          <TemplateInput
            id="newInvoiceEmail"
            path="newInvoiceEmail"
            label="template.organisation.content.email-notifications.accounting.invoices-and-accounting.label"
            subtext="template.organisation.content.email-notifications.accounting.invoices-and-accounting.subtext"
            onChange={this.onChange}
          />

          <TemplateHeader
            as="h4"
            label="template.organisation.content.email-notifications.platform.title"
          />

          <TemplateText
            size="small"
            label="template.organisation.content.email-notifications.platform.subtext"
          />

          <TemplateInput
            path="dailySummaryEmail"
            label="template.organisation.content.email-notifications.platform.daily-summary.label"
            subtext="template.organisation.content.email-notifications.platform.daily-summary.subtext"
            onChange={this.onChange}
          />

          <TemplateInput
            id="emailNotificationsFundraisingPageCreatedEmail"
            path="fundraisingPageCreatedEmail"
            label="template.organisation.content.email-notifications.platform.fundraising-page-created-email.label"
            subtext="template.organisation.content.email-notifications.platform.fundraising-page-created-email.subtext"
            onChange={this.onChange}
          />

          <TemplateInput
            id="emailNotificationsNewDonationEmail"
            path="newDonationEmail"
            label="template.organisation.content.email-notifications.platform.new-donation-email.label"
            subtext="template.organisation.content.email-notifications.platform.new-donation-email.subtext"
            onChange={this.onChange}
          />

          <TemplateInput
            id="emailNotificationsNewSaleEmail"
            path="newSaleEmail"
            label="template.organisation.content.email-notifications.platform.new-sale-email.label"
            subtext="template.organisation.content.email-notifications.platform.new-sale-email.subtext"
            onChange={this.onChange}
          />

          <TemplateInput
            id="emailNotificationsAmbassadorProgramEmail"
            path="ambassadorProgramEmail"
            label="template.organisation.content.email-notifications.platform.ambassador-program-email-email.label"
            subtext="template.organisation.content.email-notifications.platform.ambassador-program-email-email.subtext"
            onChange={this.onChange}
          />

          <TemplateInput
            id="emailNotificationsAmbassadorProgramContactEmail"
            path="ambassadorProgramContactEmail"
            label="template.organisation.content.email-notifications.platform.ambassador-program-email-contact-email.label"
            subtext="template.organisation.content.email-notifications.platform.ambassador-program-email-contact-email.subtext"
            onChange={this.onChange}
          />
        </TemplatePage>

        {this.props.isSystemAdmin && (
          <TemplatePage
            id="eyes-only"
            i18nKey="template.organisation.content.eyes-only.page-title"
            systemOnly
          >
            <TemplateHeader
              as="h3"
              label="template.organisation.content.eyes-only.custom-campaigns.header"
            />

            <TemplateCheckbox
              id="useAsCustomOrganization"
              path="template.value.customDomain.useAsCustomOrganization"
              inputLabel="template.organisation.content.eyes-only.custom-campaigns.checkbox"
              onChange={this.onUseAsCustomOrganizationChange}
            />

            <TemplateInput
              id="customUrlDomain"
              path="template.value.customDomain.domain"
              label="template.organisation.content.eyes-only.custom-campaigns.domain.label"
              subtext="template.organisation.content.eyes-only.custom-campaigns.domain.subtext"
              subtextPosition="bottom"
              validation={this.domainValidation}
              onChange={this.onChange}
            />

            <TemplateHorizontalRule path="template.value.allowCRMIntegration" />

            <TemplateHeader
              as="h3"
              path="template.value.allowCRMIntegration"
              label="template.organisation.content.eyes-only.allowCRMIntegration.header"
            />

            <TemplateCheckbox
              id="allowCRMIntegration"
              path="template.value.allowCRMIntegration"
              inputLabel="template.organisation.content.eyes-only.allowCRMIntegration.checkbox"
              onChange={this.onAllowCRMIntegrationChange}
            />

            <TemplateHorizontalRule path="template.value.donationSetup.disableBankAccountPayment" />

            <TemplateHeader
              as="h3"
              path="template.value.donationSetup.disableBankAccountPayment"
              label="template.organisation.content.eyes-only.donation-setup.header"
            />

            <TemplateCheckbox
              id="disableBankAccountPayment"
              path="template.value.donationSetup.disableBankAccountPayment"
              inputLabel="template.organisation.content.eyes-only.donation-setup.checkbox"
              onChange={this.onChange}
            />

            <TemplateHorizontalRule />

            <TemplateHeader
              as="h3"
              label="template.organisation.content.eyes-only.event-campaign-creation.header"
            />

            <TemplateDropdown
              id="allowOrgToBeAddedToEvents"
              path="eventCampaignCreate"
              label="template.organisation.content.eyes-only.event-campaign-creation.allowOrgToBeAddedToEvents.label"
              onChange={this.onChange}
              options={[
                { text: I18n.t('template.organisation.content.eyes-only.event-campaign-creation.allowOrgToBeAddedToEvents.options.null'), value: null },
                { text: I18n.t('template.organisation.content.eyes-only.event-campaign-creation.allowOrgToBeAddedToEvents.options.true'), value: true },
                { text: I18n.t('template.organisation.content.eyes-only.event-campaign-creation.allowOrgToBeAddedToEvents.options.false'), value: false }
              ]}
            />

            <TemplateHorizontalRule />

            <TemplateHeader
              as="h3"
              label="template.organisation.content.eyes-only.mfa-settings.header"
            />

            <TemplateDropdown
              id="hasMfaActive"
              path="hasMfaActive"
              label="template.organisation.content.eyes-only.mfa-settings.label"
              subtext="template.organisation.content.eyes-only.mfa-settings.subtext"
              subtextPosition="bottom"
              onChange={this.onChange}
              options={[
                { text: I18n.t('template.organisation.content.eyes-only.mfa-settings.options.false'), value: false },
                { text: I18n.t('template.organisation.content.eyes-only.mfa-settings.options.true'), value: true }
              ]}
            />

            <TemplateDropdown
              id="passkeySatisfiesMfa"
              path="passkeySatisfiesMfa"
              label="template.organisation.content.eyes-only.mfa-settings.passkey.label"
              subtext="template.organisation.content.eyes-only.mfa-settings.passkey.subtext"
              subtextPosition="bottom"
              onChange={this.onChange}
              options={[
                { text: I18n.t('template.organisation.content.eyes-only.mfa-settings.passkey.options.true'), value: true },
                { text: I18n.t('template.organisation.content.eyes-only.mfa-settings.passkey.options.false'), value: false }
              ]}
            />
          </TemplatePage>
        )}
        <TemplatePage
          id="finance"
          i18nKey="template.organisation.content.finance.page-title"
        >
          <TemplateHeader
            as="h3"
            label="template.organisation.content.finance.header"
          />

          <TemplateStripeConnection path="paymentPlatforms" />

          {isSwiftaidVisible && <SwiftaidConnection />}
        </TemplatePage>
        <TemplatePage
          id="crm"
          i18nKey="template.organisation.content.crm.page-title"
        >
          <TemplateHeader
            as="h3"
            label="template.organisation.content.crm.header"
          />

          <TemplateSalesforceConnection path="crmPlatforms" />
        </TemplatePage>
      </TemplateBuilderContainerEx>
    );
  }
}

const mapState = (state) => {
  return {
    address: state.templateBuilderEx.data.address,
    isSystemAdmin: state.session?.isSystemAdmin,
    isOrgAdmin: state.session?.isOrganizationAdmin,
    donationSubscriptionSettings: templateHelper.getValue(
      state.templateBuilderEx,
      'donationSubscriptionSettings'
    ),
    organization: state.organization?.record?.data?.data,
    taxId: templateHelper.getValue(state.templateBuilderEx, 'taxId'),
    isUkCharityEnabled: selectIsFeatureFlagExplicitlyEnabled(
      state,
      FeatureFlagKeys.UkCharity
    ),
    isSwiftaidConnectEnabled: selectIsSwiftaidConnectEnabled(state)
  };
};

const mapDispatch = (dispatch) => {
  return bindActionCreators(templateBuilderActions, dispatch);
};

const OrganizationTemplate = withRouter(
  connect(mapState, mapDispatch)(OrganizationTemplateView)
);
export default OrganizationTemplate;
